/* =========================================================================== *\
 __      __.__     ___________           .__    ____ ________  ________.________
/  \    /  \__| ___\__    ___/___   ____ |  |__/_   /   __   \/   __   \   ____/
\   \/\/   /  |/    \|    |_/ __ \_/ ___\|  |  \|   \____    /\____    /____  \ 
 \        /|  |   |  \    |\  ___/\  \___|   Y  \   |  /    /    /    //       \
  \__/\  / |__|___|  /____| \___  >\___  >___|  /___| /____/    /____//______  /
       \/          \/           \/     \/     \/                             \/ 
\* =========================================================================== */

import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

// @mui material components
import Card from '@mui/material/Card';
import Icon from '@mui/material/Icon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Pagination from '@mui/material/Pagination';
import Grid from '@mui/material/Grid';
import InputAdornment from '@mui/material/InputAdornment';
import Checkbox from '@mui/material/Checkbox';

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDInput from 'components/MDInput';
import TableHeader from 'components/Table/TableHeader';
import MDDateRange from 'components/MDDateRange';
import MDTypography from 'components/MDTypography';

// Material Dashboard 2 PRO React examples
import Navbar from 'examples/Navbars/NavBar';
import { APP_STATE } from 'services/Define';
import { FetchData } from 'services/Request';
import * as Func from 'services/Function';

const initFind = {
  search: '',
  order_by: 'pd.name',
  order_sort: 'asc',
  page: 1,
  perPage: 10,
  pageTotal: 0,
  period: 'DATE',
  branch: [],
  date_start: Func.dateTimeFirstMonth(),
  date_stop: Func.dateTimeEndMonth(),
};

const initSummary = {
  total_invoice: '',
  grand_quantity: '',
  grand_amount: '',
  grand_vat: '',
  grand_net: '',
};

function ReportSalesByProduct() {
  const mod = 'rp_sales_by_product';
  const app = useSelector((state) => state[APP_STATE]);
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const [pageTotal, setPageTotal] = useState(0);
  const [pageNo, setPageNo] = useState(1);
  const [record, setRecord] = useState([]);
  const [show, setShow] = useState(null);
  const [find, setFind] = useState(initFind);
  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState(initFind.order_sort);
  const [orderBy, setOrderBy] = useState(initFind.order_by);
  const [cboBranch, setCboBranch] = useState([]);
  const [summary, setSummary] = useState(initSummary);
  const [urlExcel, setUrlExcel] = useState('');
  // const [urlExpress, setUrlExpress] = useState("");

  const openShow = (e) => setShow(e.currentTarget);
  const closeShow = () => setShow(null);

  const initHeader = [
    { id: 'tb.id', label: t(`attr.image`), align: 'center', width: 40 },
    { id: 'pd.name', label: 'สินค้า', align: 'left' },
    { id: 'branch', label: 'สาขา', align: 'center' },
    { id: 'invoice_count', label: 'จำนวนบิล', align: 'right' },
    { id: 'total_quantity', label: 'จำนวน', align: 'right' },
    { id: 'total_cost', label: 'ทุน', align: 'right' },
    { id: 'total_price', label: 'ราคา', align: 'right' },
    { id: 'total_amount', label: 'มูลค่า', align: 'right' },
    { id: 'total_vat', label: 'ภาษี', align: 'right' },
    { id: 'total_net', label: 'รวม', align: 'right' },
  ];

  const view = (data) => {
    setFind(data);
    setLoading(true);
    setRecord([]);
    setSummary(initSummary);
    closeShow();

    FetchData('POST', `/${mod}`, app.token, {
      lang: app.lang,
      search: data.search,
      page: data.page,
      per_page: data.perPage,
      orderby: data.order_by,
      ordersort: data.order_sort,
      branch: data.branch,
      period: data.period,
      date_start: data.date_start,
      date_stop: data.date_stop,
      status: data.status,
    }).then((result) => {
      if (result.status === 'ok') {
        setRecord(result.data);

        setPageTotal(result.total_pages);
        setPageNo(result.page);

        setSummary(result.summary);
      }
      setLoading(false);
    });
  };

  const excel = () => {
    const params = new URLSearchParams();
    params.append('search', find.search);
    params.append('period', find.period);
    if (find.period !== 'ALL') {
      params.append('date_start', find.date_start);
      params.append('date_stop', find.date_stop);
    }
    find.branch.forEach((branchId) => {
      params.append('branch[]', branchId);
    });

    const url = `${urlExcel}/download?${params.toString()}`;
    window.open(url, '_self');
  };

  const setDateRange = (start, stop) => {
    view({
      ...find,
      date_start: Func.dateTimeFormat(start.toDate()),
      date_stop: Func.dateTimeFormat(stop.toDate()),
      page: 1,
    });
  };

  const renderLoading = () => {
    return (
      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color='inherit' />
      </Backdrop>
    );
  };

  const renderShow = (
    <Menu
      anchorEl={show}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      open={Boolean(show)}
      onClose={closeShow}
      keepMounted
    >
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 10 })}>
        {t(`element.show_record`, { num: 10 })}
      </MenuItem>
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 20 })}>
        {t(`element.show_record`, { num: 20 })}
      </MenuItem>
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 100 })}>
        {t(`element.show_record`, { num: 100 })}
      </MenuItem>
    </Menu>
  );

  const handleSort = (e, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);

    view({ ...find, order_by: property, order_sort: isAsc ? 'desc' : 'asc' });
  };

  const init = () => {
    FetchData('GET', `/${mod}`, app.token, {
      lang: app.lang,
    }).then((result) => {
      if (result.status === 'ok') {
        setCboBranch(result.branch);
        // setCboStatus(result.stepper);
        setUrlExcel(result.excel);
        // setUrlExpress(result.express);

        const newFind = {
          ...find,
          branch: result.branch.map((b) => b.id),
          page: 1,
        };

        setFind(newFind);
        view(newFind);
      } else {
        Func.exitApp();
      }
    });
  };

  const renderSearch = () => {
    return (
      <Card sx={{ p: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={6} lg={3}>
            <MDBox
              noValidate
              component='form'
              role='form'
              onSubmit={(e) => {
                e.preventDefault();
                view({ ...find, period: 'ALL', page: 1 });
              }}
            >
              <MDInput
                name='searchform'
                fullWidth
                value={find.search}
                type='search'
                onChange={(e) => setFind({ ...find, search: e.target.value, page: 1 })}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position='start'>{t('element.search')} :</InputAdornment>
                  ),
                  autoComplete: 'off',
                }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={3}>
            <MDInput
              select
              fullWidth
              SelectProps={{
                multiple: true,
                renderValue: (selected) => {
                  if (selected.length === cboBranch.length) return 'ทั้งหมด';
                  return cboBranch
                    .filter((item) => selected.includes(item.id))
                    .map((item) => item.name)
                    .join(', ');
                },
              }}
              value={find.branch}
              onChange={(e) => {
                const { value } = e.target;

                if (value.includes('ALL')) {
                  const allSelected = find.branch.length === cboBranch.length;
                  view({
                    ...find,
                    branch: allSelected ? [] : cboBranch.map((item) => item.id),
                    page: 1,
                  });
                } else {
                  view({ ...find, branch: value, page: 1 });
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>{t('menu.branch')} :</InputAdornment>
                ),
              }}
            >
              <MenuItem value='ALL'>
                <Checkbox checked={find.branch.length === cboBranch.length} />
                ทั้งหมด
              </MenuItem>
              {cboBranch.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  <Checkbox checked={find.branch.indexOf(item.id) > -1} />
                  {item.name}
                </MenuItem>
              ))}
            </MDInput>
          </Grid>

          {find.period === 'ALL' ? (
            <Grid item xs={12} sm={12} md={6} lg={2}>
              <MDButton
                variant='outlined'
                color='secondary'
                fullWidth
                sx={{ p: 1.5 }}
                onClick={() => view({ ...find, period: 'DATE', page: 1 })}
              >
                <Icon>edit_calendar</Icon>&nbsp;เลือกช่วงเวลา
              </MDButton>
            </Grid>
          ) : null}
          {find.period === 'ALL' ? null : (
            <Grid item xs={12} sm={12} md={12} lg={5}>
              <MDDateRange
                dateStart={find.date_start}
                dateStop={find.date_stop}
                callBack={setDateRange}
                closeDate={() => view({ ...find, period: 'ALL', page: 1 })}
              />
            </Grid>
          )}
          {find.branch === 'ALL' &&
          Math.abs(new Date(find.date_start) - new Date(find.date_stop)) / (1000 * 60 * 60 * 24) >
            2 ? null : (
            <Grid item xs={12} sm={12} md={12} lg={1}>
              <MDButton variant='gradient' color='success' fullWidth onClick={excel}>
                <Icon>table_view</Icon>&nbsp;Excel
              </MDButton>
            </Grid>
          )}
        </Grid>
      </Card>
    );
  };

  const renderList = () => {
    return (
      <MDBox mb={7}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={3} md={2} lg={1}>
            <MDButton
              variant='contained'
              color='dark'
              size='large'
              fullWidth
              sx={{ pt: 1.6, pb: 1.6 }}
              href='/report_pos'
            >
              <Icon>content_paste_search</Icon>
            </MDButton>
          </Grid>
          <Grid item xs={12} sm={9} md={10} lg={11}>
            {renderSearch()}
          </Grid>
          <Grid item xs={12}>
            <TableContainer component={Paper} sx={{ minHeight: 400 }}>
              <Table aria-label='datatable table'>
                <TableHeader
                  order={order}
                  orderBy={orderBy}
                  onRequestSort={handleSort}
                  data={initHeader}
                />
                <TableBody>
                  {record.map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell>{Func.imageDisplay(row)}</TableCell>
                      <TableCell align='left'>
                        <MDBox className='nowrap'>
                          {row.code} <span className='color-default'>{row.sku}</span>
                        </MDBox>
                        <MDBox className='cut' width={200}>
                          {row.name}
                        </MDBox>
                      </TableCell>
                      <TableCell align='center' className='nowrap'>
                        {row.branch_name}
                      </TableCell>
                      <TableCell align='right'>
                        <MDBox className='nowrap'>{Func.numberDisplay(row.invoice_count)}</MDBox>
                      </TableCell>
                      <TableCell align='right' className='nowrap highlight-info'>
                        <MDBox className='nowrap'>{Func.numberDisplay(row.total_quantity)}</MDBox>
                        <MDBox className='nowrap'>{row.unit_name}</MDBox>
                      </TableCell>
                      <TableCell align='right' className='nowrap'>
                        <MDBox className='nowrap'>{Func.numberDisplay(row.total_cost)}</MDBox>
                        <MDBox className='nowrap'>บาท</MDBox>
                      </TableCell>
                      <TableCell align='right' className='nowrap'>
                        <MDBox className='nowrap'>{Func.numberDisplay(row.total_price)}</MDBox>
                        <MDBox className='nowrap'>บาท</MDBox>
                      </TableCell>
                      <TableCell align='right' className='nowrap'>
                        <MDBox className='nowrap'>{Func.numberDisplay(row.total_amount)}</MDBox>
                        <MDBox className='nowrap'>บาท</MDBox>
                      </TableCell>
                      <TableCell align='right' className='nowrap'>
                        <MDBox className='nowrap'>{Func.numberDisplay(row.total_vat)}</MDBox>
                        <MDBox className='nowrap'>บาท</MDBox>
                      </TableCell>
                      <TableCell align='right' className='nowrap highlight-success'>
                        <MDBox className='nowrap'>{Func.numberDisplay(row.total_net)}</MDBox>
                        <MDBox className='nowrap'>บาท</MDBox>
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell align='right' colSpan={3}>
                      รวม
                    </TableCell>
                    <TableCell align='right'>{Func.numberDisplay(summary.total_invoice)}</TableCell>
                    <TableCell align='right' className='highlight-info'>
                      {Func.numberDisplay(summary.grand_quantity)}
                    </TableCell>
                    <TableCell colSpan={2} />
                    <TableCell align='right'>{Func.numberDisplay(summary.grand_amount)}</TableCell>
                    <TableCell align='right'>{Func.numberDisplay(summary.grand_vat)}</TableCell>
                    <TableCell align='right' className='highlight-success'>
                      {Func.numberDisplay(summary.grand_net)}
                    </TableCell>
                    <TableCell colSpan={2} />
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
          <Grid item xs={12}>
            <Card sx={{ p: 2 }}>
              <MDBox display='flex' justifyContent='space-between' alignItems='flex-start'>
                <MDButton variant={show ? 'contained' : 'outlined'} color='dark' onClick={openShow}>
                  {t(`element.show_record`, { num: find.perPage })}&nbsp;
                  <Icon>keyboard_arrow_down</Icon>
                </MDButton>
                {renderShow}
                <Pagination
                  count={pageTotal}
                  variant='outlined'
                  color='secondary'
                  showFirstButton
                  showLastButton
                  page={pageNo}
                  onChange={(e, p) => view({ ...find, page: p })}
                />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  useEffect(() => {
    i18n.changeLanguage(app.lang);

    init();
  }, [id]);

  return (
    <MDBox>
      <Navbar route={[t(`menu.report_pos`), t(`menu.rp_sales_by_product`)]} />
      {renderList()}
      {renderLoading()}
    </MDBox>
  );
}

export default ReportSalesByProduct;
